import {
  Badge,
  Box,
  Card,
  Grid,
  Group,
  Image,
  Skeleton,
  Stack,
  Text,
} from '@mantine/core'
import type { FC } from 'react'
import LocationIcon from '~/icon/location.svg'
import ScheduleIcon from '~/icon/schedule.svg'

type EventCardProps = {
  url: string
  image: string
  operator: string
  title: string
  category?: string
  location?: string
  date?: string
  time?: string
  logoUrl?: string
}

export const SkeletonEventCard: FC = () => {
  return (
    <Card shadow='sm' padding='xs' radius='md' withBorder>
      <Card.Section mb='md'>
        <Skeleton height={108} />
      </Card.Section>

      <Stack gap='xs'>
        <Skeleton height={16} />
        <Skeleton height={26} />
      </Stack>

      <Stack gap='sm' w='100%' mt='sm'>
        <Group gap='4'>
          <Skeleton height={16} radius={'sm'} width={64} />
        </Group>

        <Grid justify='space-between' my='xs'>
          <Grid.Col span={8}>
            <Stack gap='4'>
              <Group gap='4'>
                <LocationIcon />
                <Skeleton height={12} />
              </Group>
              <Group align='flex-start'>
                <ScheduleIcon />
                <Skeleton height={12} />
                <Skeleton height={12} />
              </Group>
            </Stack>
          </Grid.Col>
          <Grid.Col span={4}>
            <Box>
              <Skeleton height={48} width={48} />
            </Box>
          </Grid.Col>
        </Grid>
      </Stack>
    </Card>
  )
}

export const EventCard: FC<EventCardProps> = ({
  url,
  image,
  operator,
  title,
  category,
  location,
  date,
  time,
  logoUrl,
}) => {
  const hasEventSession =
    location !== undefined && date !== undefined && time !== undefined

  return (
    <Card
      shadow='sm'
      px='md'
      radius='xs'
      withBorder
      component='a'
      href={url}
      h='100%'
    >
      <Card.Section>
        <Image src={image} mah={280} alt={`${title} のヘッダー画像`} />
      </Card.Section>

      <Stack justify='space-between' h='100%'>
        <Stack gap='xs' mt='md'>
          <Text c='primary' fw='bold' size='xs' lh='18px'>
            {operator}
          </Text>
          <Text fw='bold' size='md' lineClamp={3} lh='26px'>
            {title}
          </Text>
        </Stack>

        <Stack mt='auto' justify='space-between'>
          {category ? (
            <Group gap='4'>
              <Badge
                variant='outline'
                color='secondary'
                size='sm'
                radius='sm'
                fw='bold'
              >
                {category}
              </Badge>
            </Group>
          ) : null}

          <Group justify='space-between' gap='4' mb='xs'>
            <Stack gap='4'>
              {hasEventSession ? (
                <>
                  <Group gap='4'>
                    <LocationIcon />
                    <Text c='primary' fw='bold' size='xs'>
                      {location}
                    </Text>
                  </Group>
                  <Group gap='4' align='flex-start'>
                    <ScheduleIcon />
                    <Text c='primary' fw='bold' size='xs'>
                      {date}
                      <br />
                      {time}
                    </Text>
                  </Group>
                </>
              ) : null}
            </Stack>
            {logoUrl ? (
              <Box>
                <Image
                  src={logoUrl}
                  alt={`${operator}のロゴ画像`}
                  fit='contain'
                  height={48}
                  width={48}
                  maw={48}
                />
              </Box>
            ) : null}
          </Group>
        </Stack>
      </Stack>
    </Card>
  )
}
